import { fetchAvailableVehicleImageCategories } from '@/components/vehicles/vehicle-gallery/api/use-load-vehicle-image-categories';
import { toast } from '@/hooks/use-toast';
import { vehicleImageStore } from '@/models/stores/vehicle-images';
import type { VehicleImageCategory } from '@/models/types';
import React, { useCallback, useEffect, useState } from 'react';
import { type VehicleImage } from '../components/vehicles/vehicle-gallery/types';

export enum UploadStatus {
  NONE = 'none',
  PREPARE = 'prepare',
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  MARKING_FEATURED = 'marking_featured',
  RE_ORDERING = 're_ordering',
  FINALIZING = 'finalizing',
}

export namespace UploadStatus {
  export function isProcessingUpload(status: UploadStatus): boolean {
    return ![UploadStatus.NONE, UploadStatus.PREPARE].includes(status);
  }
}

type VehicleGalleryContextType = {
  vehicleListingID: string;
  vehicleImages: VehicleImage[];
  setVehicleImages: React.Dispatch<React.SetStateAction<VehicleImage[]>>;
  uploadStatus: UploadStatus;
  setUploadStatus: React.Dispatch<React.SetStateAction<UploadStatus>>;
  forceReload: () => void;
  /**
   * This flag should be used only in the beginning when the images are being loaded for the first time.
   */
  isLoadingImages: boolean;
  availableImageCategories: VehicleImageCategory[];
  showImageCategoryEditor: boolean;
  toggleShowCategoryEditor: () => void;
  selectedImageIDs: string[];
  clearSelectedImageIDs: () => void;
  toggleSelectedImageID: (imgID: string) => void;
  isImageSelected: (imgID: string) => boolean;
};

const VehicleGalleryContext = React.createContext<VehicleGalleryContextType>({
  vehicleListingID: '',
  vehicleImages: [],
  setVehicleImages: () => {},
  uploadStatus: UploadStatus.NONE,
  setUploadStatus: () => {},
  forceReload: () => {},
  isLoadingImages: false,
  availableImageCategories: [],
  showImageCategoryEditor: false,
  toggleShowCategoryEditor: () => {},
  selectedImageIDs: [],
  clearSelectedImageIDs: () => {},
  toggleSelectedImageID: () => {},
  isImageSelected: () => false,
});

type VehicleGalleryContextProviderProps = React.PropsWithChildren & {
  // This will tell us for listing to pull images
  vehicleListingID: string;
};

export const VehicleGalleryContextProvider = ({
  children,
  vehicleListingID,
}: VehicleGalleryContextProviderProps) => {
  // We keep component level version of the images because some actions require editing them locally before finally commiting them.
  // Such action is image upload: user is able to preview images, re-order them and then proceed with upload.
  const [vehicleImages, setVehicleImages] = useState<VehicleImage[]>([]);
  // We decided to load available vehicle image categories from API so FE and BE doesn't have to keep them separately
  const [availableImageCategories, setAvailableImageCategories] = useState<
    VehicleImageCategory[]
  >([]);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    UploadStatus.NONE,
  );
  const [isLoadingImages, setIsloadingImages] = useState(false);

  // Below are states related to image category management
  const [showImageCategoryEditor, setShowImageCategoryEditor] = useState(false);
  const toggleShowCategoryEditor = useCallback(() => {
    setShowImageCategoryEditor(!showImageCategoryEditor);
  }, [showImageCategoryEditor]);

  const [selectedImageIDs, setSelectedImageIDs] = useState<string[]>([]);
  const isImageSelected = useCallback(
    (imgID: string) => {
      return selectedImageIDs.some((currentImgID) => currentImgID === imgID);
    },
    [selectedImageIDs],
  );
  const toggleSelectedImageID = useCallback(
    (imgID: string) => {
      if (isImageSelected(imgID)) {
        setSelectedImageIDs(
          selectedImageIDs.filter((currentImgID) => currentImgID !== imgID),
        );
        return;
      }

      setSelectedImageIDs([...selectedImageIDs, imgID]);
    },
    [selectedImageIDs],
  );
  const clearSelectedImageIDs = useCallback(() => {
    setSelectedImageIDs([]);
  }, []);

  const loadImages = useCallback(
    (isForced: boolean = false) => {
      setIsloadingImages(true);

      vehicleImageStore
        .loadImagesForListing(vehicleListingID, isForced)
        .then(() => {
          setVehicleImages(
            vehicleImageStore.getVehicleImages(vehicleListingID),
          );
        })
        .catch((err) => {
          console.error(err);
          toast({
            title: 'Error loading images',
            description:
              'There was an error while loading images, please try refreshing the page.',
            variant: 'destructive',
          });
        })
        .finally(() => {
          setIsloadingImages(false);
        });
    },
    [vehicleListingID],
  );

  const forceReload = useCallback(() => {
    loadImages(true);
  }, []);

  useEffect(() => {
    loadImages();
  }, [vehicleListingID]);

  useEffect(() => {
    fetchAvailableVehicleImageCategories()
      .then((categories) => {
        setAvailableImageCategories(categories);
      })
      .catch((err) => {
        let message =
          'There was an error while loading available image categories.';

        const responseError = err as { message: string };
        if (responseError.message) {
          message = responseError.message;
        }

        toast({
          title: 'Error loading image categories',
          description: message,
          variant: 'destructive',
        });
      });
  }, []);

  return (
    <VehicleGalleryContext.Provider
      value={{
        vehicleListingID,
        vehicleImages,
        setVehicleImages,
        isLoadingImages,
        uploadStatus,
        setUploadStatus,
        forceReload,
        availableImageCategories,
        showImageCategoryEditor,
        toggleShowCategoryEditor,
        selectedImageIDs,
        toggleSelectedImageID,
        isImageSelected,
        clearSelectedImageIDs,
      }}
    >
      {children}
    </VehicleGalleryContext.Provider>
  );
};

export const useVehicleGalleryContext = () => {
  return React.useContext(VehicleGalleryContext);
};
